/**
 * 브라우저에서 **흰 배경을 지운다** — 판정은 `packages/shared/src/cutout.ts` 것을 그대로 쓴다.
 *
 * ## 왜 여기 있나
 * 운영자는 AI(Google AI Studio·ChatGPT)로 그림을 뽑아 **흰 배경 그대로** 올린다.
 * 그러면 마을에 **흰 네모가 뜬다.** 지금까지는 개발자가 스크립트로 지워 왔고,
 * 운영자에게 넘기는 순간 무너지는 구조였다(docs/build/28 §13).
 *
 * ## 판정을 여기서 다시 짜지 않는다
 * 관리자에서 지운 결과와 웹에 올라가는 그림이 **같아야** 한다. 그래서 픽셀을 다루는
 * 규칙은 전부 `shared` 것을 부르고, 여기서는 **캔버스로 픽셀을 꺼내 주는 일만** 한다
 * (CLAUDE.md — 미리보기를 만드는 화면은 렌더러와 같은 판정 함수를 쓴다).
 *
 * ## 캔버스를 못 쓰면 막지 않는다
 * 테스트(jsdom)나 옛 브라우저에서는 캔버스가 없을 수 있다. 그때는 `undefined` 를 돌려주고
 * **경고만** 하도록 둔다 — 판정을 못 했다고 업로드를 막으면 운영자가 갇힌다
 * (`validateAsset` 이 `hasBackground === undefined` 를 그렇게 다룬다).
 */
import {
  inspectBackground,
  cutoutBackground,
  type BackgroundReport,
  type CutoutOptions,
  type Pixels,
} from "@greg-chick/shared";

/** 큰 그림 전체를 보지 않는다 — 가장자리 판정에는 줄인 그림으로 충분하고 훨씬 빠르다. */
const INSPECT_MAX = 512;

function canvasOf(
  w: number,
  h: number,
): { c: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  return ctx ? { c, ctx } : null;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (typeof Image === "undefined") return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** 그림의 픽셀을 꺼낸다. 캔버스를 못 쓰면 null. */
async function pixelsOf(dataUrl: string, maxSide?: number): Promise<Pixels | null> {
  const img = await loadImage(dataUrl);
  if (!img || !img.naturalWidth) return null;
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  if (maxSide && Math.max(w, h) > maxSide) {
    const s = maxSide / Math.max(w, h);
    w = Math.max(1, Math.round(w * s));
    h = Math.max(1, Math.round(h * s));
  }
  const cv = canvasOf(w, h);
  if (!cv) return null;
  cv.ctx.drawImage(img, 0, 0, w, h);
  try {
    const d = cv.ctx.getImageData(0, 0, w, h);
    return { data: d.data, width: w, height: h };
  } catch {
    // 다른 출처의 그림이면 캔버스가 오염돼 픽셀을 못 읽는다(브라우저 보안).
    // 업로드한 파일은 dataURL 이라 여기 걸리지 않지만, 걸리면 막지 말고 넘긴다.
    return null;
  }
}

/** 배경이 남아 있는지 본다. 판정을 못 했으면 `undefined`(막지 않고 경고만). */
export async function inspectFile(dataUrl: string): Promise<BackgroundReport | undefined> {
  const px = await pixelsOf(dataUrl, INSPECT_MAX);
  return px ? inspectBackground(px) : undefined;
}

export interface CutoutFileResult {
  /** 지운 뒤의 파일. 그대로 업로드하면 된다. */
  file: File;
  dataUrl: string;
  removedRatio: number;
  width: number;
  height: number;
}

/**
 * 배경을 지우고 **여백까지 잘라낸** 새 파일을 만든다.
 *
 * 여백을 자르는 이유: 안 자르면 48px 로 줄일 때 **그림이 그만큼 작아진다**
 * (가운데 조그맣게 놓인 소품이 된다).
 */
export async function cutoutFile(file: File, dataUrl: string): Promise<CutoutFileResult | null> {
  const px = await pixelsOf(dataUrl); // 원본 크기 그대로 — 실제로 쓸 그림이라 줄이지 않는다
  if (!px) return null;

  const { removed, bounds } = cutoutBackground(px);
  if (removed === 0 || !bounds) return null;

  const w = bounds.right - bounds.left + 1;
  const h = bounds.bottom - bounds.top + 1;
  const cv = canvasOf(w, h);
  if (!cv) return null;

  // 지운 픽셀을 캔버스에 되돌린 뒤, 남은 사각형만 오려낸다
  const full = canvasOf(px.width, px.height);
  if (!full) return null;
  // `new ImageData(px.data, …)` 는 버퍼 종류까지 맞아야 해서 타입이 걸린다.
  // 캔버스가 만들어 준 빈 것에 값을 부어 넣으면 그 문제가 없다.
  const id = full.ctx.createImageData(px.width, px.height);
  id.data.set(px.data);
  full.ctx.putImageData(id, 0, 0);
  cv.ctx.drawImage(full.c, bounds.left, bounds.top, w, h, 0, 0, w, h);

  // PNG 로 낸다 — 투명을 담고, 여기서 다시 손실 압축을 하지 않는다
  const blob = await new Promise<Blob | null>((res) => cv.c.toBlob(res, "image/png"));
  if (!blob) return null;
  const name = file.name.replace(/\.[^.]+$/, "") + "_cut.png";
  return {
    file: new File([blob], name, { type: "image/png" }),
    dataUrl: cv.c.toDataURL("image/png"),
    removedRatio: removed / (px.width * px.height),
    width: w,
    height: h,
  };
}

/* ── 🪄 이미 올라간 그림의 배경 지우기 ─────────────────── */

export interface CutoutEntryResult {
  url: string;
  prev: string;
  /** 지운 비율(0~1). */
  ratio: number;
  /** 사람에게 할 말. 문제없으면 `null`. */
  warning: string | null;
}

/**
 * 🪄 **자리 하나의 배경을 지워 다시 올린다** — 어느 화면에서 부르든 같은 길.
 *
 * ## 왜 따로 있나 — `cutoutFile` 은 **올릴 때** 쓴다
 * 지금까지 배경 지우기는 **새 파일을 올리는 순간에만** 있었다(규격 검사에 걸린 파일).
 * 그래서 **이미 올라가 있는 그림**은 손댈 방법이 없었다 — 운영자가 배경 남은 걸 발견해도
 * 지우고 다시 올리거나, 개발자에게 말하는 수밖에 없었다(그레그 흰색 발밑이 그 경우다).
 *
 * ## 🔴 **자르지 않는다**(trim)
 * `cutoutFile` 은 여백을 잘라낸다 — 새 소품을 48px 에 맞춰 넣을 때 안 자르면 조그맣게
 * 보이기 때문이다. 그런데 **이미 올라간 그림에 그러면 안 된다**: 걷기 스트립은 칸이
 * 가로로 이어져 있어서, 여백이 잘리면 **칸 나누기가 통째로 어긋난다**(걸음이 깨진다).
 * 크기를 그대로 두는 것이 여기서는 규칙이다.
 *
 * 🔴 **직전 그림을 `prev` 에 남긴다** — [↩ 아까 그림으로]로 되돌아가게(뒤집기와 같은 규칙).
 */
export async function cutoutAssetEntry(
  slotKey: string,
  url: string,
  deps: {
    loadImage: (u: string) => Promise<HTMLImageElement>;
    upload: (key: string, file: File) => Promise<string>;
  },
  opt: CutoutOptions = {},
): Promise<CutoutEntryResult> {
  const img = await deps.loadImage(url);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const cv = canvasOf(w, h);
  if (!cv) throw new Error("이 브라우저에서는 배경을 지울 수 없어요.");
  cv.ctx.drawImage(img, 0, 0);
  let id: ImageData;
  try {
    id = cv.ctx.getImageData(0, 0, w, h);
  } catch {
    // 다른 출처의 그림이라 브라우저가 픽셀을 막았다 — 왜 안 되는지 말해 준다
    throw new Error(
      "이 그림은 픽셀을 읽을 수 없어요(다른 주소에서 온 그림). 내려받아 다시 올려 주세요.",
    );
  }
  const { removed } = cutoutBackground({ data: id.data, width: w, height: h }, opt);
  const ratio = removed / Math.max(1, w * h);
  // 🔴 통째로 지워질 판이면 **올리지 않는다** — 되돌릴 수 있어도 아예 안 망치는 편이 낫다
  if (ratio > 0.995) {
    throw new Error(
      "그림이 통째로 지워질 뻔했어요. 배경과 그림 색이 거의 같아요(흰 배경에 흰 그림 등).",
    );
  }
  cv.ctx.putImageData(id, 0, 0);
  // PNG 로 낸다 — 투명을 담고, 여기서 다시 손실 압축을 하지 않는다(cutoutFile 과 같은 이유)
  const blob = await new Promise<Blob | null>((res) => cv.c.toBlob(res, "image/png"));
  if (!blob) throw new Error("그림을 저장하지 못했어요. 파일이 깨졌을 수 있어요.");
  const next = await deps.upload(slotKey, new File([blob], "cutout.png", { type: "image/png" }));
  return { url: next, prev: url, ratio, warning: cutoutWarning(ratio) };
}

/**
 * 🔴 **안 된 것을 안 됐다고 말한다.** 조용히 이상한 그림을 돌려주면
 * 운영자는 왜 이상한지 모른 채 그대로 게시한다.
 */
export function cutoutWarning(ratio: number): string | null {
  if (ratio < 0.005) {
    return "지울 배경을 거의 못 찾았어요. 이미 투명하거나, 그림이 테두리까지 꽉 차 있어요.";
  }
  if (ratio > 0.9) {
    return "거의 다 지워졌어요. 그림과 배경 색이 너무 비슷한 것 같아요 — 이상하면 [↩ 아까 그림으로] 눌러 주세요.";
  }
  return null;
}
